import * as Sentry from '@sentry/browser';
import {isEqual, once} from 'lodash';
import {useEffect, useLayoutEffect, useReducer, useRef} from 'react';
import {unstable_batchedUpdates} from 'react-dom';
import {useIntl} from 'react-intl';
import {graphql} from 'react-relay';
import {fetchQuery} from 'relay-runtime';
import environment from '../../../../RelayEnvironment';
import DeleteAgenticWorkerResultMutation from '../../../../mutations/agentic-workers/DeleteAgenticWorkerResultMutation';
import HideAgenticWorkerResultMutation from '../../../../mutations/agentic-workers/HideAgenticWorkerResultMutation';
import StartAgenticWorkerRunMutation from '../../../../mutations/agentic-workers/StartAgenticWorkerRunMutation';
import StopAgenticWorkerRunMutation from '../../../../mutations/agentic-workers/StopAgenticWorkerRunMutation';
import type {DeleteAgenticWorkerResultMutation$data} from '../../../../mutations/agentic-workers/__generated__/DeleteAgenticWorkerResultMutation.graphql';
import type {
	AgenticWorkerRowType,
	StartAgenticWorkerRunMutation$data,
} from '../../../../mutations/agentic-workers/__generated__/StartAgenticWorkerRunMutation.graphql';
import type {StopAgenticWorkerRunMutation$data} from '../../../../mutations/agentic-workers/__generated__/StopAgenticWorkerRunMutation.graphql';
import Util from '../../util/util';
import {createToast} from '../toasts/toast';
import type {agentRunStateQuery} from './__generated__/agentRunStateQuery.graphql';
import type {agentRunStateSettleQuery} from './__generated__/agentRunStateSettleQuery.graphql';
import {AGENTIC_WORKERS_MESSAGE} from './agenticWorkersMessages';
import {
	AGENT_RESULT_STATE,
	AGENT_RUN_SCOPE,
	AGENT_RUN_STATE,
	type AgentDeclaration,
	type AgentLastRun,
	type AgentResult,
	type AgentResultState,
	type AgentRow,
	type AgentRun,
	type AgentRunOutcome,
	type AgentRunScope,
	type AgentRunState,
	type AgentRunStateReason,
} from './agentDeclaration';

export const POLL_INTERVAL_MILLISECONDS = 3000;
export const SETTLE_READ_ATTEMPTS = 3;
const TOAST_DURATION_MILLISECONDS = 5000;
const VISIBILITY_CHANGE_EVENT = 'visibilitychange';
const VISIBLE_STATE: DocumentVisibilityState = 'visible';

export const PAGE_RUN_SCOPE: AgentRunScope = {kind: AGENT_RUN_SCOPE.PAGE};

export const AGENT_ROW_STATUS = {
	IDLE: 'IDLE',
	WORKING: 'WORKING',
	READY: 'READY',
} as const;
export type AgentRowStatus = (typeof AGENT_ROW_STATUS)[keyof typeof AGENT_ROW_STATUS];

export type AgentRowState = {
	status: AgentRowStatus;
	result: AgentResult | null;
	inPageRun: boolean;
};

export type AgentRunSnapshot = {
	activeRuns: AgentRun[];
	// Newest run first.
	waitingResults: AgentResult[];
	lastRun: AgentLastRun | null;
	// True from a run leaving the active runs until a read sent after that answers, because until then the last run can
	// still be the one before it.
	lastRunPending: boolean;
	pendingStartKeys: ReadonlySet<string>;
	heldStopKeys: ReadonlySet<string>;
	stoppingRunIds: ReadonlySet<string>;
	restartAfterStopKeys: ReadonlySet<string>;
};

type AgentRunListener = () => void;

type StopListener = () => void;

type PageRunFinishedListener = (outcome: AgentRunOutcome) => void;

export type AgentDeleteListeners = {
	onDeleted: () => void;
	onActedFirst: (actedByName: string | null, state: AgentResultState | null) => void;
};

const STOP_ANSWER = {
	STOPPED: 'STOPPED',
	FINISHED_FIRST: 'FINISHED_FIRST',
	FAILED: 'FAILED',
} as const;
type StopAnswer = (typeof STOP_ANSWER)[keyof typeof STOP_ANSWER];

type AgentRunEntry = {
	snapshot: AgentRunSnapshot;
	listeners: Set<AgentRunListener>;
	pendingStarts: Set<string>;
	// By scope key: a Stop clicked before its start answered.
	heldStops: Map<string, StopListener>;
	// By run id: a stop sent and not yet answered.
	stopsInFlight: Map<string, StopListener>;
	// By scope key: a Run clicked while that scope's stop was held or in flight, sent once a sent stop settles.
	restartsAfterStop: Map<string, AgentRunScope>;
	// By result id: a Delete or Hide sent and not yet answered. Each applies to the snapshot at once, and over every read
	// until its answer.
	deletesInFlight: Set<string>;
	hidesInFlight: Set<string>;
	isReading: boolean;
	// Set when the store asks for a read because its snapshot is not read yet or out of date, so polling carries on
	// until a read succeeds.
	needsFreshRead: boolean;
	// Polling retries a failing read every few seconds, so a run of failures is reported once.
	hasReportedFailedRead: boolean;
	pollTimer: ReturnType<typeof setTimeout> | null;
	// A read sent before the latest answered start, stop, Delete or Hide can be stale, so its answer is thrown away.
	answeredChanges: number;
	// graphql reads the last run alongside the active runs, so a read sent before a run left can answer the run before
	// it as the last run. Counts the runs that left, and the count when the latest applied read was sent.
	runEnds: number;
	runEndsWhenReadSent: number;
	// The page runs this tab has seen running. Only these are settled when they leave the active runs.
	watchedPageRunIds: Set<string>;
	// A watched page run that has left the active runs. The next read asks how it ended.
	settlingRunId: string | null;
	failedSettleReads: number;
	// By run id: a produced outcome whose results the settle read did not list, told on the next successful read.
	heldOutcomes: Map<string, AgentRunOutcome>;
	finishListeners: Set<PageRunFinishedListener>;
};

type RunNode = {
	readonly id: string;
	readonly scopeKind: string;
	readonly scopeRowType?: string | null;
	readonly scopeRowId?: number | null;
	readonly state: string;
	readonly rows?: ReadonlyArray<{
		readonly rowType: string;
		readonly rowId: number;
	}> | null;
};

type SettledRunNode = RunNode & {
	readonly stateReason: string | null;
	readonly foundDetail: string | null;
};

type ResultNode = {
	readonly id: string;
	readonly runId: string;
	readonly rows: ReadonlyArray<{
		readonly rowType: string;
		readonly rowId: number;
	}>;
	readonly state: string;
	readonly hidden: boolean;
	readonly producedAt: string | null;
};

type ReadResponse = {
	readonly viewer: {
		readonly agenticWorker: {
			readonly activeRuns: ReadonlyArray<RunNode>;
			readonly waitingResults: ReadonlyArray<ResultNode>;
			readonly lastRun: SettledRunNode | null;
			readonly run?: SettledRunNode | null;
		} | null;
	} | null;
};

// Spread unmasked, so the reads get these fields as plain data and nothing reads the fragments themselves.
graphql`
	fragment agentRunState_run on AgenticWorkerRun {
		id
		scopeKind
		scopeRowType
		scopeRowId
		state
		rows {
			rowType
			rowId
		}
	}
`;

graphql`
	fragment agentRunState_worker on AgenticWorker {
		activeRuns {
			...agentRunState_run @relay(mask: false)
		}
		lastRun {
			...agentRunState_run @relay(mask: false)
			stateReason
			foundDetail
		}
		waitingResults {
			id
			runId
			rows {
				rowType
				rowId
			}
			state
			hidden
			producedAt
		}
	}
`;

const query = graphql`
	query agentRunStateQuery($agentId: String!) {
		viewer {
			agenticWorker(agentId: $agentId) {
				...agentRunState_worker @relay(mask: false)
			}
		}
	}
`;

const settleQuery = graphql`
	query agentRunStateSettleQuery($agentId: String!, $runId: ID!) {
		viewer {
			agenticWorker(agentId: $agentId) {
				...agentRunState_worker @relay(mask: false)
				run(id: $runId) {
					...agentRunState_run @relay(mask: false)
					stateReason
					foundDetail
				}
			}
		}
	}
`;

const EMPTY_SNAPSHOT: AgentRunSnapshot = {
	activeRuns: [],
	waitingResults: [],
	lastRun: null,
	lastRunPending: false,
	pendingStartKeys: new Set(),
	heldStopKeys: new Set(),
	stoppingRunIds: new Set(),
	restartAfterStopKeys: new Set(),
};

const entries = new Map<string, AgentRunEntry>();

const entryFor = (agentId: string): AgentRunEntry => {
	const existing = entries.get(agentId);
	if (existing) {
		return existing;
	}
	const created: AgentRunEntry = {
		snapshot: EMPTY_SNAPSHOT,
		listeners: new Set(),
		pendingStarts: new Set(),
		heldStops: new Map(),
		stopsInFlight: new Map(),
		restartsAfterStop: new Map(),
		deletesInFlight: new Set(),
		hidesInFlight: new Set(),
		isReading: false,
		needsFreshRead: false,
		hasReportedFailedRead: false,
		pollTimer: null,
		answeredChanges: 0,
		runEnds: 0,
		runEndsWhenReadSent: 0,
		watchedPageRunIds: new Set(),
		settlingRunId: null,
		failedSettleReads: 0,
		heldOutcomes: new Map(),
		finishListeners: new Set(),
	};
	entries.set(agentId, created);
	return created;
};

const toRowScope = (row: AgentRow): AgentRunScope => ({kind: AGENT_RUN_SCOPE.ROW, ...row});

export const toScopeKey = (scope: AgentRunScope): string =>
	scope.kind === AGENT_RUN_SCOPE.PAGE ? AGENT_RUN_SCOPE.PAGE : `${AGENT_RUN_SCOPE.ROW}#${scope.rowType}#${scope.rowId}`;

// A row run that lost its row would read as a page run, so it is dropped.
const toAgentRun = (node: RunNode): AgentRun | null => {
	const state = node.state as AgentRunState;
	const rows = (node.rows ?? []).map(row => ({
		rowType: row.rowType,
		rowId: row.rowId,
	}));
	if (node.scopeKind !== AGENT_RUN_SCOPE.ROW) {
		return {
			id: node.id,
			scope: PAGE_RUN_SCOPE,
			state,
			rows,
		};
	}
	if (!node.scopeRowType || typeof node.scopeRowId !== 'number') {
		return null;
	}
	return {
		id: node.id,
		scope: {
			kind: AGENT_RUN_SCOPE.ROW,
			rowType: node.scopeRowType,
			rowId: node.scopeRowId,
		},
		state,
		rows,
	};
};

const isAgentRun = (run: AgentRun | null): run is AgentRun => run !== null;

const toAgentResult = (node: ResultNode): AgentResult => ({
	id: node.id,
	runId: node.runId,
	rows: node.rows.map(row => ({
		rowType: row.rowType,
		rowId: row.rowId,
	})),
	state: node.state as AgentResultState,
	hidden: node.hidden,
	producedAt: node.producedAt,
});

// The detail is JSON the agent owns, so a detail that does not parse is left out rather than losing the summary.
const parseFoundDetail = (foundDetail: string | null): unknown => {
	if (foundDetail === null) {
		return null;
	}
	try {
		return JSON.parse(foundDetail);
	} catch {
		return null;
	}
};

const toAgentLastRun = (node: SettledRunNode): AgentLastRun | null => {
	const run = toAgentRun(node);
	if (run === null) {
		return null;
	}
	return {
		id: run.id,
		scope: run.scope,
		state: run.state,
		stateReason: node.stateReason as AgentRunStateReason | null,
		detail: parseFoundDetail(node.foundDetail),
	};
};

const toScopeInput = (scope: AgentRunScope) =>
	scope.kind === AGENT_RUN_SCOPE.PAGE
		? {scopeKind: AGENT_RUN_SCOPE.PAGE}
		: {
				scopeKind: AGENT_RUN_SCOPE.ROW,
				// Host pages name their row types; the records accept the values the server's enum lists.
				scopeRowType: scope.rowType as AgenticWorkerRowType,
				scopeRowId: scope.rowId,
		  };

const needsPolling = (entry: AgentRunEntry): boolean =>
	entry.needsFreshRead ||
	entry.settlingRunId !== null ||
	entry.snapshot.activeRuns.length > 0 ||
	entry.pendingStarts.size > 0 ||
	entry.stopsInFlight.size > 0;

const cancelPoll = (entry: AgentRunEntry): void => {
	if (entry.pollTimer !== null) {
		clearTimeout(entry.pollTimer);
		entry.pollTimer = null;
	}
};

const schedulePoll = (agentId: string, entry: AgentRunEntry): void => {
	if (entry.pollTimer !== null || entry.isReading || entry.listeners.size === 0 || !needsPolling(entry)) {
		return;
	}
	entry.pollTimer = setTimeout(() => {
		entry.pollTimer = null;
		// schedulePoll and readAgentRuns call each other.
		// eslint-disable-next-line @typescript-eslint/no-use-before-define
		readAgentRuns(agentId);
	}, POLL_INTERVAL_MILLISECONDS);
};

type AgentRunRead = Pick<AgentRunSnapshot, 'activeRuns' | 'waitingResults' | 'lastRun'>;

const isLastRunPending = (entry: AgentRunEntry): boolean => entry.runEndsWhenReadSent !== entry.runEnds;

const withResultCommandsInFlight = (entry: AgentRunEntry, waitingResults: AgentResult[]): AgentResult[] =>
	waitingResults
		.filter(result => !entry.deletesInFlight.has(result.id))
		.map(result => (entry.hidesInFlight.has(result.id) && !result.hidden ? {...result, hidden: true} : result));

const publish = (agentId: string, entry: AgentRunEntry, changes: Partial<AgentRunRead> = {}): void => {
	entry.snapshot = {
		...entry.snapshot,
		...changes,
		waitingResults: withResultCommandsInFlight(entry, changes.waitingResults ?? entry.snapshot.waitingResults),
		lastRunPending: isLastRunPending(entry),
		pendingStartKeys: new Set(entry.pendingStarts),
		heldStopKeys: new Set(entry.heldStops.keys()),
		stoppingRunIds: new Set(entry.stopsInFlight.keys()),
		restartAfterStopKeys: new Set(entry.restartsAfterStop.keys()),
	};
	// Outside a React event, React 16 renders each listener's update on its own, so they are batched into one render.
	unstable_batchedUpdates(() => {
		entry.listeners.forEach(listener => listener());
	});
	schedulePoll(agentId, entry);
};

const applyRead = (agentId: string, entry: AgentRunEntry, read: AgentRunRead): void => {
	const waitingResults = withResultCommandsInFlight(entry, read.waitingResults);
	const isUnchanged =
		isEqual(entry.snapshot.activeRuns, read.activeRuns) &&
		isEqual(entry.snapshot.waitingResults, waitingResults) &&
		isEqual(entry.snapshot.lastRun, read.lastRun) &&
		entry.snapshot.lastRunPending === isLastRunPending(entry);
	if (isUnchanged) {
		return;
	}
	publish(agentId, entry, {
		...read,
		waitingResults,
	});
};

const hasRunLeft = (activeRunsBefore: AgentRun[], activeRunsAfter: AgentRun[]): boolean =>
	activeRunsBefore.some(run => !activeRunsAfter.some(active => active.id === run.id));

// With no control subscribed, the read waits for the next control to subscribe, which reads at once.
const requestFreshRead = (agentId: string, entry: AgentRunEntry): void => {
	entry.needsFreshRead = true;
	if (entry.listeners.size > 0) {
		// requestFreshRead and readAgentRuns call each other.
		// eslint-disable-next-line @typescript-eslint/no-use-before-define
		readAgentRuns(agentId);
	}
};

const clearSettling = (entry: AgentRunEntry): void => {
	entry.settlingRunId = null;
	entry.failedSettleReads = 0;
};

const endWatch = (entry: AgentRunEntry, runId: string): void => {
	entry.watchedPageRunIds.delete(runId);
	if (entry.settlingRunId === runId) {
		clearSettling(entry);
	}
};

// Forgotten when the last control unsubscribes, so a control that subscribes later never tells of a run it did not
// show ending.
const forgetWatchedPageRuns = (entry: AgentRunEntry): void => {
	entry.watchedPageRunIds.clear();
	entry.heldOutcomes.clear();
	clearSettling(entry);
};

const watchPageRun = (entry: AgentRunEntry, runId: string): void => {
	if (entry.listeners.size > 0) {
		entry.watchedPageRunIds.add(runId);
	}
};

const watchPageRuns = (entry: AgentRunEntry): void => {
	entry.snapshot.activeRuns
		.filter(run => run.scope.kind === AGENT_RUN_SCOPE.PAGE)
		.forEach(run => watchPageRun(entry, run.id));
};

const settleNextEndedPageRun = (agentId: string, entry: AgentRunEntry): void => {
	const endedRunId = Array.from(entry.watchedPageRunIds).find(
		runId => !entry.snapshot.activeRuns.some(run => run.id === runId)
	);
	if (endedRunId === undefined) {
		return;
	}
	entry.settlingRunId = endedRunId;
	requestFreshRead(agentId, entry);
};

const notifyPageRunFinished = (entry: AgentRunEntry, outcome: AgentRunOutcome): void => {
	entry.finishListeners.forEach(listener => {
		// Listeners run agent-owned code, and one that throws must not stop the store reading.
		try {
			listener(outcome);
		} catch (error) {
			Sentry.captureException(error);
		}
	});
};

// Told once, with whatever the read lists for the run: a run whose results are gone by now is told with none.
const notifyHeldOutcomes = (entry: AgentRunEntry, waitingResults: AgentResult[]): void => {
	const held = Array.from(entry.heldOutcomes);
	entry.heldOutcomes.clear();
	held.forEach(([runId, outcome]) =>
		notifyPageRunFinished(entry, {
			...outcome,
			results: waitingResults.filter(result => result.runId === runId),
		})
	);
};

const settlePageRun = (
	agentId: string,
	entry: AgentRunEntry,
	runId: string,
	run: SettledRunNode | null,
	waitingResults: AgentResult[]
): void => {
	if (run?.state === AGENT_RUN_STATE.RUNNING) {
		clearSettling(entry);
		// graphql answers no active runs when its read of them fails, so a run that is still going is put back.
		const runningRun = toAgentRun(run);
		if (runningRun && !entry.snapshot.activeRuns.some(active => active.id === runId)) {
			publish(agentId, entry, {activeRuns: [...entry.snapshot.activeRuns, runningRun]});
		}
		return;
	}
	endWatch(entry, runId);
	if (run?.state === AGENT_RUN_STATE.PRODUCED) {
		// graphql answers no waiting results when its read of them fails, so one more read makes sure the run's
		// results show.
		entry.needsFreshRead = true;
		const outcome: AgentRunOutcome = {
			state: AGENT_RUN_STATE.PRODUCED,
			stateReason: run.stateReason as AgentRunStateReason | null,
			results: waitingResults.filter(result => result.runId === runId),
			detail: parseFoundDetail(run.foundDetail),
		};
		if (outcome.results.length === 0) {
			// A summary must not report that failed read as finding none, so the trailing read tells it with the
			// results it lists for the run.
			entry.heldOutcomes.set(runId, outcome);
			return;
		}
		notifyPageRunFinished(entry, outcome);
	}
};

const failSettleRead = (agentId: string, entry: AgentRunEntry, runId: string): void => {
	entry.failedSettleReads += 1;
	if (entry.failedSettleReads < SETTLE_READ_ATTEMPTS) {
		return;
	}
	Sentry.captureMessage(`Settling page run "${runId}" of agent "${agentId}" failed; no summary will show`, 'warning');
	endWatch(entry, runId);
};

// A module-level store shared by many controls cannot use the useForecastFetchQuery hook, and this read is
// supplementary, not the page's loading query.
const fetchAgentRuns = (agentId: string, settlingRunId: string | null): Promise<ReadResponse | undefined> =>
	settlingRunId === null
		? fetchQuery<agentRunStateQuery>(environment.getInstance(), query, {agentId}).toPromise()
		: fetchQuery<agentRunStateSettleQuery>(environment.getInstance(), settleQuery, {
				agentId,
				runId: settlingRunId,
		  }).toPromise();

function readAgentRuns(agentId: string): void {
	const entry = entryFor(agentId);
	if (entry.isReading) {
		return;
	}
	cancelPoll(entry);
	entry.isReading = true;
	const changesWhenSent = entry.answeredChanges;
	const runEndsWhenSent = entry.runEnds;
	fetchAgentRuns(agentId, entry.settlingRunId).then(
		response => {
			if (entries.get(agentId) !== entry) {
				return;
			}
			entry.isReading = false;
			entry.hasReportedFailedRead = false;
			if (entry.answeredChanges !== changesWhenSent) {
				requestFreshRead(agentId, entry);
				return;
			}
			entry.needsFreshRead = false;
			// The server answers no worker while the feature is off for this viewer, so nothing runs or waits.
			const worker = response?.viewer?.agenticWorker;
			const activeRuns = worker?.activeRuns.map(toAgentRun).filter(isAgentRun) ?? [];
			const waitingResults = worker?.waitingResults.map(toAgentResult) ?? [];
			if (worker && hasRunLeft(entry.snapshot.activeRuns, activeRuns)) {
				entry.runEnds += 1;
				entry.needsFreshRead = true;
			}
			entry.runEndsWhenReadSent = runEndsWhenSent;
			applyRead(agentId, entry, {
				activeRuns,
				waitingResults,
				lastRun: worker?.lastRun ? toAgentLastRun(worker.lastRun) : null,
			});
			watchPageRuns(entry);
			notifyHeldOutcomes(entry, waitingResults);
			if (entry.settlingRunId !== null) {
				settlePageRun(agentId, entry, entry.settlingRunId, worker?.run ?? null, waitingResults);
			}
			settleNextEndedPageRun(agentId, entry);
			schedulePoll(agentId, entry);
		},
		() => {
			if (entries.get(agentId) !== entry) {
				return;
			}
			entry.isReading = false;
			if (entry.settlingRunId !== null) {
				failSettleRead(agentId, entry, entry.settlingRunId);
			} else if (!entry.hasReportedFailedRead) {
				entry.hasReportedFailedRead = true;
				Sentry.captureMessage(`Reading the runs of agent "${agentId}" failed; keeping the last known runs`, 'warning');
			}
			schedulePoll(agentId, entry);
		}
	);
}

const findActiveRun = (entry: AgentRunEntry, scopeKey: string): AgentRun | undefined =>
	entry.snapshot.activeRuns.find(run => toScopeKey(run.scope) === scopeKey);

const isStopUnderway = (entry: AgentRunEntry, scopeKey: string): boolean => {
	if (entry.pendingStarts.has(scopeKey)) {
		return entry.heldStops.has(scopeKey);
	}
	const run = findActiveRun(entry, scopeKey);
	return run !== undefined && entry.stopsInFlight.has(run.id);
};

type StandardErrorFlow = (error: Error) => boolean | void;

const finishStop = (agentId: string, run: AgentRun, answer: StopAnswer): void => {
	const entry = entryFor(agentId);
	const scopeKey = toScopeKey(run.scope);
	const onStopped = entry.stopsInFlight.get(run.id);
	entry.stopsInFlight.delete(run.id);
	// Any other answer keeps a page run watched, so a stop that lost the race to the finish still settles it.
	if (answer === STOP_ANSWER.STOPPED) {
		endWatch(entry, run.id);
	}
	if (answer === STOP_ANSWER.FAILED) {
		// The person has just seen the standard error modal, and starting again would most likely show a second one.
		entry.restartsAfterStop.delete(scopeKey);
	}
	entry.answeredChanges += 1;
	// Whatever the answer, the run leaves the snapshot here, and one more read tells whether it still runs and which run
	// was the last.
	entry.runEnds += 1;
	publish(agentId, entry, {activeRuns: entry.snapshot.activeRuns.filter(active => active.id !== run.id)});
	requestFreshRead(agentId, entry);
	const restart = entry.restartsAfterStop.get(scopeKey);
	if (restart) {
		// Removed only now, so the control reads Working throughout instead of flickering idle.
		entry.restartsAfterStop.delete(scopeKey);
		// A start can hold a stop, and a stop can hold a start, so the two call each other.
		// eslint-disable-next-line @typescript-eslint/no-use-before-define
		startAgentRun(agentId, restart);
	}
	const isStartingAgain = entry.pendingStarts.has(scopeKey) || entry.restartsAfterStop.has(scopeKey);
	if (restart && !isStartingAgain) {
		// The start found another run going for the scope and sent nothing, so the waiting Run leaves the snapshot here.
		publish(agentId, entry);
	}
	// While the scope starts again at once, "Run stopped" would contradict the person's last click.
	if (answer === STOP_ANSWER.STOPPED && !isStartingAgain) {
		onStopped?.();
	}
};

const sendStop = (agentId: string, run: AgentRun, onStopped: StopListener): void => {
	const entry = entryFor(agentId);
	entry.stopsInFlight.set(run.id, onStopped);
	publish(agentId, entry);
	Util.CommitMutation(
		StopAgenticWorkerRunMutation,
		{id: run.id},
		(response: StopAgenticWorkerRunMutation$data) => {
			finishStop(
				agentId,
				run,
				response?.stopAgenticWorkerRun?.run?.state === AGENT_RUN_STATE.STOPPED
					? STOP_ANSWER.STOPPED
					: STOP_ANSWER.FINISHED_FIRST
			);
		},
		true,
		null,
		(error: Error, showStandardError?: StandardErrorFlow) => {
			if (showStandardError?.(error) === true) {
				return;
			}
			finishStop(agentId, run, STOP_ANSWER.FAILED);
		}
	);
};

const finishStart = (agentId: string, scopeKey: string, run: AgentRun | null): void => {
	const entry = entryFor(agentId);
	const heldStop = entry.heldStops.get(scopeKey);
	entry.pendingStarts.delete(scopeKey);
	entry.heldStops.delete(scopeKey);
	entry.answeredChanges += 1;
	if (run && run.state === AGENT_RUN_STATE.RUNNING) {
		if (run.scope.kind === AGENT_RUN_SCOPE.PAGE) {
			watchPageRun(entry, run.id);
		}
		publish(agentId, entry, {activeRuns: [...entry.snapshot.activeRuns.filter(active => active.id !== run.id), run]});
		if (heldStop) {
			sendStop(agentId, run, heldStop);
		}
		return;
	}
	// A held Stop that is never sent drops the Run waiting on it: after NOT_STARTED a run is already going for the
	// scope, and after a failure sending it again would only fail again.
	entry.restartsAfterStop.delete(scopeKey);
	// The start ended without a run of its own, so it counts as a run ending: until one more read tells what runs and
	// which run was the last, no failed card shows the failure from before it.
	entry.runEnds += 1;
	publish(agentId, entry);
	requestFreshRead(agentId, entry);
};

export const getAgentRunSnapshot = (agentId: string): AgentRunSnapshot => entries.get(agentId)?.snapshot ?? EMPTY_SNAPSHOT;

export const subscribeToPageRunFinished = (agentId: string, listener: PageRunFinishedListener): (() => void) => {
	const entry = entryFor(agentId);
	entry.finishListeners.add(listener);
	return () => {
		entry.finishListeners.delete(listener);
	};
};

const isAnyAgentSubscribed = (): boolean => Array.from(entries.values()).some(entry => entry.listeners.size > 0);

// Nothing polls while idle, so what others did while the tab was out of view shows when it comes back.
const readSubscribedAgentsOnReturn = (): void => {
	if (document.visibilityState !== VISIBLE_STATE) {
		return;
	}
	entries.forEach((entry, agentId) => {
		if (entry.listeners.size > 0) {
			requestFreshRead(agentId, entry);
		}
	});
};

export const subscribeToAgentRun = (agentId: string, listener: AgentRunListener): (() => void) => {
	const entry = entryFor(agentId);
	if (!isAnyAgentSubscribed()) {
		document.addEventListener(VISIBILITY_CHANGE_EVENT, readSubscribedAgentsOnReturn);
	}
	const isFirstListener = entry.listeners.size === 0;
	entry.listeners.add(listener);
	if (isFirstListener) {
		requestFreshRead(agentId, entry);
	}
	return () => {
		entry.listeners.delete(listener);
		if (entry.listeners.size === 0) {
			cancelPoll(entry);
			forgetWatchedPageRuns(entry);
		}
		if (!isAnyAgentSubscribed()) {
			document.removeEventListener(VISIBILITY_CHANGE_EVENT, readSubscribedAgentsOnReturn);
		}
	};
};

export const startAgentRun = (agentId: string, scope: AgentRunScope): void => {
	const entry = entryFor(agentId);
	const scopeKey = toScopeKey(scope);
	if (isStopUnderway(entry, scopeKey)) {
		if (!entry.restartsAfterStop.has(scopeKey)) {
			entry.restartsAfterStop.set(scopeKey, scope);
			publish(agentId, entry);
		}
		return;
	}
	if (entry.pendingStarts.has(scopeKey) || findActiveRun(entry, scopeKey)) {
		return;
	}
	entry.pendingStarts.add(scopeKey);
	publish(agentId, entry);
	Util.CommitMutation(
		StartAgenticWorkerRunMutation,
		{agentId, ...toScopeInput(scope)},
		(response: StartAgenticWorkerRunMutation$data) => {
			const node = response?.startAgenticWorkerRun?.run;
			finishStart(agentId, scopeKey, node ? toAgentRun(node) : null);
		},
		true,
		null,
		(error: Error, showStandardError?: StandardErrorFlow) => {
			if (showStandardError?.(error) === true) {
				return;
			}
			finishStart(agentId, scopeKey, null);
		}
	);
};

export const stopAgentRun = (agentId: string, scope: AgentRunScope, onStopped: StopListener): void => {
	const entry = entryFor(agentId);
	const scopeKey = toScopeKey(scope);
	if (entry.restartsAfterStop.has(scopeKey)) {
		entry.restartsAfterStop.delete(scopeKey);
		publish(agentId, entry);
		return;
	}
	if (entry.pendingStarts.has(scopeKey)) {
		entry.heldStops.set(scopeKey, onStopped);
		publish(agentId, entry);
		return;
	}
	const run = findActiveRun(entry, scopeKey);
	if (run && !entry.stopsInFlight.has(run.id)) {
		sendStop(agentId, run, onStopped);
	}
};

type ResultCommand<MutationResponse> = {
	inFlightOf: (entry: AgentRunEntry) => Set<string>;
	mutation: unknown;
	retry: boolean;
	onAnswer: (response: MutationResponse) => void;
};

const sendResultCommand = <MutationResponse>(
	agentId: string,
	resultId: string,
	command: ResultCommand<MutationResponse>
): void => {
	const entry = entryFor(agentId);
	const inFlight = command.inFlightOf(entry);
	if (inFlight.has(resultId)) {
		return;
	}
	inFlight.add(resultId);
	publish(agentId, entry);
	// Whatever the answer, one more read tells what waits now.
	const finish = (): void => {
		inFlight.delete(resultId);
		entry.answeredChanges += 1;
		requestFreshRead(agentId, entry);
	};
	Util.CommitMutation(
		command.mutation,
		{id: resultId},
		(response: MutationResponse) => {
			finish();
			command.onAnswer(response);
		},
		command.retry,
		null,
		(error: Error, showStandardError?: StandardErrorFlow) => {
			if (showStandardError?.(error) === true) {
				return;
			}
			finish();
		}
	);
};

export const deleteAgentResult = (agentId: string, resultId: string, listeners: AgentDeleteListeners): void =>
	sendResultCommand(agentId, resultId, {
		inFlightOf: entry => entry.deletesInFlight,
		mutation: DeleteAgenticWorkerResultMutation,
		// A Delete whose answer was lost and is sent again answers that nothing was deleted, as if someone else had
		// acted first.
		retry: false,
		onAnswer: (response: DeleteAgenticWorkerResultMutation$data) => {
			const answer = response?.deleteAgenticWorkerResult;
			if (answer?.deleted) {
				listeners.onDeleted();
				return;
			}
			listeners.onActedFirst(
				answer?.result?.actedBy?.fullName ?? null,
				(answer?.result?.state as AgentResultState | undefined) ?? null
			);
		},
	});

export const hideAgentResult = (agentId: string, resultId: string, onHidden: () => void): void =>
	sendResultCommand(agentId, resultId, {
		inFlightOf: entry => entry.hidesInFlight,
		mutation: HideAgenticWorkerResultMutation,
		// Hiding again changes nothing, so a lost answer can be retried.
		retry: true,
		onAnswer: onHidden,
	});

export const isScopeWorking = (snapshot: AgentRunSnapshot, scope: AgentRunScope): boolean => {
	const scopeKey = toScopeKey(scope);
	if (snapshot.restartAfterStopKeys.has(scopeKey)) {
		return true;
	}
	if (snapshot.pendingStartKeys.has(scopeKey)) {
		return !snapshot.heldStopKeys.has(scopeKey);
	}
	return snapshot.activeRuns.some(run => toScopeKey(run.scope) === scopeKey && !snapshot.stoppingRunIds.has(run.id));
};

const coversRow =
	(row: AgentRow) =>
	(candidate: AgentRow): boolean =>
		candidate.rowType === row.rowType && candidate.rowId === row.rowId;

const isRowInPageRun = (snapshot: AgentRunSnapshot, row: AgentRow): boolean => {
	const isPageStopHeld =
		snapshot.pendingStartKeys.has(AGENT_RUN_SCOPE.PAGE) && snapshot.heldStopKeys.has(AGENT_RUN_SCOPE.PAGE);
	return (
		!isPageStopHeld &&
		snapshot.activeRuns.some(
			run =>
				run.scope.kind === AGENT_RUN_SCOPE.PAGE && !snapshot.stoppingRunIds.has(run.id) && run.rows.some(coversRow(row))
		)
	);
};

export const getRowState = (snapshot: AgentRunSnapshot, row: AgentRow): AgentRowState => {
	const inPageRun = isRowInPageRun(snapshot, row);
	if (isScopeWorking(snapshot, toRowScope(row)) || inPageRun) {
		return {
			status: AGENT_ROW_STATUS.WORKING,
			result: null,
			inPageRun,
		};
	}
	const result = snapshot.waitingResults.find(waiting => waiting.rows.some(coversRow(row))) ?? null;
	return {
		status: result ? AGENT_ROW_STATUS.READY : AGENT_ROW_STATUS.IDLE,
		result,
		inPageRun,
	};
};

export const countWaitingResults = (snapshot: AgentRunSnapshot): number =>
	snapshot.waitingResults.filter(result => !result.hidden).length;

export type AgentPanelState = {
	visibleResults: AgentResult[];
	failedRun: AgentLastRun | null;
};

const isAgentWorking = (snapshot: AgentRunSnapshot): boolean =>
	snapshot.activeRuns.length > 0 || snapshot.pendingStartKeys.size > 0 || snapshot.restartAfterStopKeys.size > 0;

export const getPanelState = (snapshot: AgentRunSnapshot): AgentPanelState => {
	const {lastRun} = snapshot;
	// A hidden result still waits, so a failed run before it is left to the past runs.
	const showsFailedRun =
		lastRun?.state === AGENT_RUN_STATE.FAILED &&
		!snapshot.lastRunPending &&
		snapshot.waitingResults.length === 0 &&
		!isAgentWorking(snapshot);
	return {
		visibleResults: snapshot.waitingResults.filter(result => !result.hidden && result.rows.length > 0),
		failedRun: showsFailedRun ? lastRun : null,
	};
};

export const clearAgentRunStore = (): void => {
	entries.forEach(entry => {
		cancelPoll(entry);
		entry.listeners.clear();
		entry.finishListeners.clear();
		forgetWatchedPageRuns(entry);
	});
	entries.clear();
	document.removeEventListener(VISIBILITY_CHANGE_EVENT, readSubscribedAgentsOnReturn);
};

type AgentRunCommands = {
	startRun: () => void;
	stopRun: () => void;
};

export type AgentRowControls = AgentRowState & AgentRunCommands;

type AgentPageState = {
	isRunning: boolean;
	waitingCount: number;
};

export type AgentPageControls = AgentPageState & AgentRunCommands;

const showToast = (message: string): void =>
	createToast({
		duration: TOAST_DURATION_MILLISECONDS,
		message,
	});

const useRunStoppedToast = (): (() => void) => {
	const {formatMessage} = useIntl();
	return () => showToast(formatMessage({id: AGENTIC_WORKERS_MESSAGE.TOAST_RUN_STOPPED}));
};

// One toast per tab only while a single control per agent is mounted: each mounted control subscribes and toasts.
export const useSummaryToast = (declaration: AgentDeclaration): void => {
	const intl = useIntl();
	useEffect(
		() => subscribeToPageRunFinished(declaration.id, outcome => showToast(declaration.summaryToast(outcome, intl))),
		[declaration, intl]
	);
};

type ResultToasts = {
	showDeleted: () => void;
	showHidden: () => void;
	showActedFirst: (actedByName: string | null, state: AgentResultState | null) => void;
};

const upperCaseFirstLetter = (text: string, locale: string): string =>
	`${text.charAt(0).toLocaleUpperCase(locale)}${text.slice(1)}`;

export const useResultToasts = (declaration: AgentDeclaration): ResultToasts => {
	const {formatMessage, locale} = useIntl();
	const resultNoun = (): string => formatMessage(declaration.resultNoun.singular);
	return {
		showDeleted: () =>
			showToast(
				formatMessage(
					{id: AGENTIC_WORKERS_MESSAGE.TOAST_RESULT_DELETED},
					{result: upperCaseFirstLetter(resultNoun(), locale)}
				)
			),
		showHidden: () => showToast(formatMessage({id: AGENTIC_WORKERS_MESSAGE.TOAST_RESULT_HIDDEN})),
		showActedFirst: (actedByName, state) =>
			showToast(
				formatMessage(
					{
						id:
							state === AGENT_RESULT_STATE.DELETED
								? AGENTIC_WORKERS_MESSAGE.TOAST_ALREADY_DELETED
								: AGENTIC_WORKERS_MESSAGE.TOAST_ALREADY_ACTED,
					},
					{
						person: actedByName ?? formatMessage({id: AGENTIC_WORKERS_MESSAGE.TOAST_SOMEONE}),
						result: resultNoun(),
					}
				)
			),
	};
};

const useAgentRunCommands = (agentId: string, scope: AgentRunScope): AgentRunCommands => {
	const showRunStoppedToast = useRunStoppedToast();
	return {
		startRun: () => startAgentRun(agentId, scope),
		stopRun: () => stopAgentRun(agentId, scope, showRunStoppedToast),
	};
};

// Selects from the latest snapshot as it renders, and re-renders only when a store change changes the selection, so a
// change to one row leaves every other row's control alone.
const useAgentRunSelection = <Selection>(
	agentId: string,
	select: (snapshot: AgentRunSnapshot) => Selection,
	isSameSelection: (rendered: Selection, latest: Selection) => boolean
): Selection => {
	const selection = select(getAgentRunSnapshot(agentId));
	const [, rerender] = useReducer((renders: number) => renders + 1, 0);
	const renderedRef = useRef({selection, select, isSameSelection});

	// Updated as the render commits, so a store change that lands before the effects run is compared with what shows.
	useLayoutEffect(() => {
		renderedRef.current = {selection, select, isSameSelection};
	});

	useEffect(() => {
		const rerenderOnChange = (): void => {
			const rendered = renderedRef.current;
			if (!rendered.isSameSelection(rendered.selection, rendered.select(getAgentRunSnapshot(agentId)))) {
				rerender();
			}
		};
		rerenderOnChange();
		return subscribeToAgentRun(agentId, rerenderOnChange);
	}, [agentId]);

	return selection;
};

const isSameRowState = (rendered: AgentRowState, latest: AgentRowState): boolean =>
	rendered.status === latest.status && rendered.inPageRun === latest.inPageRun && isEqual(rendered.result, latest.result);

const getPageState = (snapshot: AgentRunSnapshot): AgentPageState => ({
	isRunning: isScopeWorking(snapshot, PAGE_RUN_SCOPE),
	waitingCount: countWaitingResults(snapshot),
});

export const useAgentRowState = (declaration: AgentDeclaration, row: AgentRow): AgentRowControls => {
	const rowState = useAgentRunSelection(declaration.id, snapshot => getRowState(snapshot, row), isSameRowState);
	const showRunStoppedToast = useRunStoppedToast();
	const rowScope = toRowScope(row);
	return {
		...rowState,
		startRun: () => startAgentRun(declaration.id, rowScope),
		// A row in a page run stops that run too, and the two stops answer with one toast.
		stopRun: () => {
			const snapshot = getAgentRunSnapshot(declaration.id);
			const showToastOnce = once(showRunStoppedToast);
			if (isScopeWorking(snapshot, rowScope)) {
				stopAgentRun(declaration.id, rowScope, showToastOnce);
			}
			if (isRowInPageRun(snapshot, row)) {
				stopAgentRun(declaration.id, PAGE_RUN_SCOPE, showToastOnce);
			}
		},
	};
};

export const useAgentPageState = (declaration: AgentDeclaration): AgentPageControls => {
	const pageState = useAgentRunSelection(declaration.id, getPageState, isEqual);
	const commands = useAgentRunCommands(declaration.id, PAGE_RUN_SCOPE);
	return {
		...pageState,
		...commands,
	};
};

export type AgentPanelControls = AgentPanelState & {
	review: (result: AgentResult) => void;
	deleteResult: (result: AgentResult) => void;
	hideResult: (result: AgentResult) => void;
	retry: () => void;
};

export const useAgentPanelState = (declaration: AgentDeclaration): AgentPanelControls => {
	const panelState = useAgentRunSelection(declaration.id, getPanelState, isEqual);
	const {showDeleted, showHidden, showActedFirst} = useResultToasts(declaration);
	const {failedRun} = panelState;
	return {
		...panelState,
		review: result => declaration.review(result),
		deleteResult: result =>
			deleteAgentResult(declaration.id, result.id, {
				onDeleted: showDeleted,
				onActedFirst: showActedFirst,
			}),
		hideResult: result => hideAgentResult(declaration.id, result.id, showHidden),
		retry: () => {
			if (failedRun) {
				startAgentRun(declaration.id, failedRun.scope);
			}
		},
	};
};
